import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { AlertDTO } from 'src/app/model/alerts/alert-dto';
import { AlertService } from 'src/app/services/alert.service';
import { LoadIndicatorService } from 'src/app/services/load-indicator.service';
import { PaginatorComponent } from '../../paginator/paginator.component';
import { NodeDTO } from 'src/app/model/nodes/node-dto';
import { AppComponent } from 'src/app/app.component';
import { MatDialog } from '@angular/material/dialog';
import { AlertFeaturesModalComponent } from '../alert-features-modal/alert-features-modal.component';
import { CyberAttackManagementStatusEnum } from 'src/app/model/alerts/attacks/cyber-attack-management-status-enum';
import { Utils } from 'src/app/utils/utils';

interface AlertFilters {
  pending?: boolean;
  managed?: boolean;
  dateThisMonth?: boolean;
  dateThisYear?: boolean;
  dateAllTime?: boolean;
  dateFrom?: string;
  dateTo?: string;
}

@Component({
  selector: 'app-alerts',
  templateUrl: './alerts.component.html',
  styleUrl: './alerts.component.scss',
})
export class AlertsComponent implements AfterViewInit {
  public alerts: AlertDTO[] = [];
  public pageSize = 8;
  public node?: NodeDTO;
  public filters: AlertFilters = {
    pending: true,
    managed: undefined,
    dateThisMonth: false,
    dateThisYear: false,
    dateAllTime: true,
  };
  cyberAttackManagementStatus = CyberAttackManagementStatusEnum;
  utils = new Utils();

  @ViewChild('alertsPaginator')
  public paginator?: PaginatorComponent;

  constructor(
    private readonly alertService: AlertService,
    private readonly loadIndicatorService: LoadIndicatorService,
    private readonly toastr: ToastrService,
    private readonly dialog: MatDialog,
  ) {
    AppComponent.updateAppHeader('alerts');
  }

  ngAfterViewInit(): void {
    if (this.paginator) {
      this.loadAlerts();
    }
  }

  public viewAlertFeatures(alert: AlertDTO): void {
    this.dialog
      .open(AlertFeaturesModalComponent, {
        data: alert,
        minWidth: '400px',
      })
      .afterClosed()
      .subscribe(() => {
        this.loadAlerts();
      });
  }

  public alterManagedFilter(managed: boolean | undefined): void {
    this.filters.managed = managed;
    this.filtersChanged();
  }

  public resetDateFilters(): void {
    this.filters.dateThisMonth = false;
    this.filters.dateThisYear = false;
    this.filters.dateAllTime = false;
  }

  public alterMonthDateFilter(): void {
    if (this.filters.dateThisMonth !== true) {
      this.resetDateFilters();
      this.filters.dateThisMonth = true;
    }

    this.filtersChanged();
  }

  public alterYearDateFilter(): void {
    if (this.filters.dateThisYear !== true) {
      this.resetDateFilters();
      this.filters.dateThisYear = true;
    }

    this.filtersChanged();
  }

  public alterAlltimeDateFilter(): void {
    if (this.filters.dateAllTime !== true) {
      this.resetDateFilters();
      this.filters.dateAllTime = true;
    }

    this.filtersChanged();
  }

  public filtersChanged(): void {
    if (this.filters.dateAllTime) {
      this.filters.dateFrom = new Date(1999, 1, 1).toDateString();
      this.filters.dateTo = new Date(2099, 1, 1).toDateString();
    } else if (this.filters.dateThisYear) {
      let dateToday = new Date();
      dateToday.setDate(1);
      dateToday.setMonth(0);
      this.filters.dateFrom = dateToday.toDateString();

      dateToday.setFullYear(dateToday.getFullYear() + 1);
      this.filters.dateTo = dateToday.toDateString();
    } else if (this.filters.dateThisMonth) {
      let dateToday = new Date();
      dateToday.setDate(1);
      this.filters.dateFrom = dateToday.toDateString();

      dateToday.setDate(0);
      dateToday.setMonth(dateToday.getMonth() + 1);
      this.filters.dateTo = dateToday.toDateString();
    }

    this.loadAlerts();
  }

  public loadAlerts(): void {
    this.loadIndicatorService.show();
    this.alertService
      .getAlerts(
        this.paginator!.selectedPage,
        this.pageSize,
        this.node?.id,
        this.filters.dateFrom,
        this.filters.dateTo,
        this.filters.managed,
      )
      .then((response) => {
        this.alerts = response.items;
        this.paginator?.updateData(response.totalPages, response.pageSize);
      })
      .catch(() => {
        this.toastr.error('Error loading alerts');
      })
      .finally(() => {
        this.loadIndicatorService.hide();
      });
  }
}
